/**
 * 镜头调度面板（重构版）
 *
 * 设计原则：
 * - prompt 是唯一真相：画布上显示的角色 = prompt 里 @ 到的角色
 * - layout 只是稀疏批注：记录用户把哪些角色拖到了哪里；没记录的用默认位置
 * - 没有"未放置"概念、没有 + / × 按钮：增删角色都通过 prompt @
 *
 * 所有交互通过 onChange 上抛；本组件不持有内部状态（除选中态）。
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowDownLeft,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpRight,
  CircleDot,
  RotateCw,
} from 'lucide-react';
import type { CharacterLayoutItem } from '../../services/directorApi';
import { resolveBrowserMediaUrl } from '../../../../utils/storageUrl';

export interface MentionedCharacter {
  id: string;
  name: string;
  thumbnailUrl?: string | null;
}

interface Props {
  aspectRatio: '16:9' | '9:16' | '1:1' | string;
  /** prompt 里按出现顺序 @ 到的角色（已与项目角色 join 过） */
  mentionedCharacters: MentionedCharacter[];
  /** 已记录的位置（稀疏，只包含用户主动拖过的角色） */
  layout: CharacterLayoutItem[];
  cameraMovement: string | null;
  motionIntensity: string | null;
  disabled?: boolean;
  onChange: (next: {
    layout: CharacterLayoutItem[];
    cameraMovement: string | null;
    motionIntensity: string | null;
  }) => void;
}

const CAMERA_OPTIONS: Array<{
  value: string;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
}> = [
  { value: 'static', label: '不动', Icon: CircleDot },
  { value: 'push', label: '推', Icon: ArrowUpRight },
  { value: 'pull', label: '拉', Icon: ArrowDownLeft },
  { value: 'pan_left', label: '左摇', Icon: ArrowLeft },
  { value: 'pan_right', label: '右摇', Icon: ArrowRight },
  { value: 'tilt_up', label: '上摇', Icon: ArrowUp },
  { value: 'tilt_down', label: '下摇', Icon: ArrowDown },
  { value: 'orbit', label: '环绕', Icon: RotateCw },
];

const INTENSITY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'slow', label: '慢' },
  { value: 'medium', label: '中' },
  { value: 'fast', label: '快' },
];

const DEPTH_PRESETS: Array<{ key: 'fg' | 'mid' | 'bg'; label: string; scale: number }> = [
  { key: 'fg', label: '前景', scale: 1.6 },
  { key: 'mid', label: '中景', scale: 1.2 },
  { key: 'bg', label: '背景', scale: 0.8 },
];

const AVATAR_BASE = 56;

function aspectRatioToCss(aspect: string): string {
  if (aspect === '9:16') return '9 / 16';
  if (aspect === '1:1') return '1 / 1';
  return '16 / 9';
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

function gradientForName(name: string): string {
  const code = [...name].reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  const hues = [
    'from-sky-500 to-cyan-400',
    'from-amber-500 to-orange-400',
    'from-emerald-500 to-teal-400',
    'from-violet-500 to-fuchsia-400',
    'from-rose-500 to-pink-400',
  ];
  return hues[code % hues.length];
}

/**
 * 给定角色在 mention 列表中的 index，返回默认站位（中央偏右下，错位避免叠加）
 */
function defaultPositionFor(index: number): CharacterLayoutItem['x'] extends number ? { x: number; y: number; scale: number } : never {
  const offset = index * 0.06;
  return {
    x: Math.min(0.85, 0.5 + offset),
    y: Math.min(0.85, 0.5 + offset),
    scale: 1.2,
  };
}

export function CameraStagingPanel({
  aspectRatio,
  mentionedCharacters,
  layout,
  cameraMovement,
  motionIntensity,
  disabled = false,
  onChange,
}: Props) {
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // layout 字典化便于查询
  const layoutMap = useMemo(
    () => new Map(layout.map((it) => [it.characterId, it])),
    [layout]
  );

  // 渲染列表 = mentioned 的所有角色，每个角色取 layout 位置或默认位置
  const stage = useMemo(
    () =>
      mentionedCharacters.map((c, idx) => ({
        char: c,
        item:
          layoutMap.get(c.id) ?? {
            characterId: c.id,
            ...defaultPositionFor(idx),
          },
        positioned: layoutMap.has(c.id),
      })),
    [mentionedCharacters, layoutMap]
  );

  // 选中态在 mention 列表变化时自动失效
  useEffect(() => {
    if (selectedId && !mentionedCharacters.some((c) => c.id === selectedId)) {
      setSelectedId(null);
    }
  }, [mentionedCharacters, selectedId]);

  /**
   * 把当前 stage 的某个角色更新位置后，写回 layout（只保留 mentioned 的角色，避免孤儿条目）
   */
  const writeLayout = (cid: string, next: { x: number; y: number; scale: number }) => {
    if (disabled) return;
    const allowedIds = new Set(mentionedCharacters.map((c) => c.id));
    const updated: CharacterLayoutItem[] = [];
    let inserted = false;
    for (const item of layout) {
      if (!allowedIds.has(item.characterId)) continue; // prune orphan
      if (item.characterId === cid) {
        updated.push({ characterId: cid, ...next });
        inserted = true;
      } else {
        updated.push(item);
      }
    }
    if (!inserted) updated.push({ characterId: cid, ...next });
    onChange({ layout: updated, cameraMovement, motionIntensity });
  };

  // 拖拽
  const onAvatarPointerDown = (cid: string) => (e: React.PointerEvent) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    setDragId(cid);
    setSelectedId(cid);
  };

  const onCanvasPointerMove = (e: React.PointerEvent) => {
    if (!dragId || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = clamp01((e.clientX - rect.left) / rect.width);
    const y = clamp01((e.clientY - rect.top) / rect.height);
    const cur = layoutMap.get(dragId);
    writeLayout(dragId, { x, y, scale: cur?.scale ?? 1.2 });
  };

  const onCanvasPointerUp = () => setDragId(null);

  const onCanvasClick = () => setSelectedId(null);

  const selected = selectedId ? stage.find((s) => s.char.id === selectedId) : null;

  const handleCameraChange = (value: string) => {
    if (disabled) return;
    onChange({
      layout,
      cameraMovement: cameraMovement === value ? null : value,
      motionIntensity:
        cameraMovement === value ? null : motionIntensity ?? 'medium',
    });
  };

  const handleIntensityChange = (value: string) => {
    if (disabled) return;
    onChange({ layout, cameraMovement, motionIntensity: value });
  };

  return (
    <div className="space-y-4">
      {/* 画布 */}
      <div>
        <div className="mb-1.5 flex items-center justify-between">
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
            角色站位
          </p>
          {layout.length > 0 && !disabled && (
            <button
              type="button"
              onClick={() => onChange({ layout: [], cameraMovement, motionIntensity })}
              className="text-[11px] font-medium text-slate-400 transition hover:text-rose-500"
              title="重置所有角色到默认位置"
            >
              重置站位
            </button>
          )}
        </div>

        <div
          ref={canvasRef}
          onPointerMove={onCanvasPointerMove}
          onPointerUp={onCanvasPointerUp}
          onPointerLeave={onCanvasPointerUp}
          onClick={onCanvasClick}
          className={`relative overflow-hidden rounded-2xl border border-dashed border-slate-300 bg-gradient-to-br from-slate-100 to-slate-50 ${
            disabled ? 'opacity-60' : ''
          }`}
          style={{ aspectRatio: aspectRatioToCss(aspectRatio) }}
        >
          {/* 三分线 */}
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute left-1/3 top-0 h-full w-px bg-slate-200" />
            <div className="absolute left-2/3 top-0 h-full w-px bg-slate-200" />
            <div className="absolute top-1/3 left-0 h-px w-full bg-slate-200" />
            <div className="absolute top-2/3 left-0 h-px w-full bg-slate-200" />
          </div>

          {/* 镜头预览覆盖层 */}
          <CameraPreviewOverlay
            cameraMovement={cameraMovement}
            motionIntensity={motionIntensity}
          />

          {/* 空状态 */}
          {stage.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center px-6 text-center text-xs text-slate-400">
              在上方提示词输入 <span className="mx-1 font-mono text-sky-500">@</span>{' '}
              选择角色，被 @ 的角色会出现在画布上，可拖动调整站位
            </div>
          )}

          {/* 角色头像 */}
          {stage.map(({ char, item, positioned }) => {
            const size = AVATAR_BASE * item.scale;
            const isSel = selectedId === char.id;
            return (
              <div
                key={char.id}
                onPointerDown={onAvatarPointerDown(char.id)}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedId(char.id);
                }}
                className={`absolute -translate-x-1/2 -translate-y-1/2 select-none rounded-full shadow-md transition ${
                  disabled ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'
                } ${isSel ? 'ring-2 ring-sky-500 ring-offset-2 ring-offset-white' : ''} ${
                  !positioned ? 'opacity-95' : ''
                }`}
                style={{
                  left: `${item.x * 100}%`,
                  top: `${item.y * 100}%`,
                  width: `${size}px`,
                  height: `${size}px`,
                  zIndex: Math.round(item.scale * 100),
                }}
                title={positioned ? char.name : `${char.name}（默认位置，拖动即可记录）`}
              >
                {char.thumbnailUrl ? (
                  <img
                    src={resolveBrowserMediaUrl(char.thumbnailUrl)}
                    alt={char.name}
                    draggable={false}
                    className="h-full w-full rounded-full object-cover"
                  />
                ) : (
                  <div
                    className={`flex h-full w-full items-center justify-center rounded-full bg-gradient-to-br font-semibold text-white ${gradientForName(
                      char.name
                    )}`}
                    style={{ fontSize: Math.max(12, size * 0.32) }}
                  >
                    {char.name.slice(0, 1)}
                  </div>
                )}
                {/* 顶部小角标显示出场顺序 */}
                <span className="absolute -left-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-sky-500 text-[10px] font-semibold text-white shadow ring-2 ring-white">
                  {mentionedCharacters.findIndex((c) => c.id === char.id) + 1}
                </span>
              </div>
            );
          })}
        </div>

        {/* 选中角色的深度切换 + 缩放滑块 */}
        {selected && !disabled && (
          <div className="mt-2 space-y-2 rounded-2xl border border-slate-200 bg-white p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-700">{selected.char.name}</span>
              <span className="text-[11px] text-slate-400">
                {selected.item.scale >= 1.4
                  ? '前景'
                  : selected.item.scale >= 1.0
                    ? '中景'
                    : '背景'}
              </span>
            </div>
            <div className="flex gap-1.5">
              {DEPTH_PRESETS.map((preset) => {
                const isActive =
                  preset.key === 'fg'
                    ? selected.item.scale >= 1.4
                    : preset.key === 'mid'
                      ? selected.item.scale >= 1.0 && selected.item.scale < 1.4
                      : selected.item.scale < 1.0;
                return (
                  <button
                    key={preset.key}
                    type="button"
                    onClick={() =>
                      writeLayout(selected.char.id, {
                        x: selected.item.x,
                        y: selected.item.y,
                        scale: preset.scale,
                      })
                    }
                    className={`flex-1 rounded-xl border px-2 py-1.5 text-xs font-medium transition ${
                      isActive
                        ? 'border-sky-500 bg-sky-50 text-sky-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-sky-200'
                    }`}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between text-[11px] text-slate-400">
                <span>缩放</span>
                <span>{selected.item.scale.toFixed(2)}×</span>
              </div>
              <input
                type="range"
                min={0.6}
                max={2.0}
                step={0.05}
                value={selected.item.scale}
                onChange={(e) =>
                  writeLayout(selected.char.id, {
                    x: selected.item.x,
                    y: selected.item.y,
                    scale: parseFloat(e.target.value),
                  })
                }
                className="w-full accent-sky-500"
              />
            </div>
            <p className="text-[11px] text-slate-400">
              要从画布移除此角色，请在提示词里删掉对应的 <span className="font-mono text-sky-500">@</span>{selected.char.name}
            </p>
          </div>
        )}
      </div>

      {/* 镜头运动 */}
      <div>
        <p className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
          镜头运动
        </p>
        <div className="grid grid-cols-4 gap-1.5">
          {CAMERA_OPTIONS.map((opt) => {
            const Icon = opt.Icon;
            const active = cameraMovement === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleCameraChange(opt.value)}
                disabled={disabled}
                className={`flex flex-col items-center justify-center gap-0.5 rounded-2xl border px-1.5 py-2 text-[11px] font-medium transition ${
                  active
                    ? 'border-slate-950 bg-slate-950 text-white shadow-md'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'
                } disabled:cursor-not-allowed disabled:opacity-50`}
              >
                <Icon className="h-4 w-4" />
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* 速度 */}
      <div>
        <p className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
          速度
        </p>
        <div className="grid grid-cols-3 gap-1.5">
          {INTENSITY_OPTIONS.map((opt) => {
            const active = motionIntensity === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleIntensityChange(opt.value)}
                disabled={disabled || !cameraMovement || cameraMovement === 'static'}
                className={`rounded-2xl border px-3 py-2 text-sm font-medium transition ${
                  active
                    ? 'border-slate-950 bg-slate-950 text-white shadow-md'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'
                } disabled:cursor-not-allowed disabled:opacity-50`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        {(cameraMovement === 'static' || !cameraMovement) && (
          <p className="mt-1 text-[11px] text-slate-400">
            选择"不动"或未选镜头运动时无需设速度
          </p>
        )}
      </div>
    </div>
  );
}

export function summarizeStaging(params: {
  layoutCount: number;
  cameraMovement: string | null;
  motionIntensity: string | null;
}): string {
  const parts: string[] = [];
  if (params.layoutCount > 0) parts.push(`${params.layoutCount} 个角色已布置`);
  if (params.cameraMovement) {
    const cam = CAMERA_OPTIONS.find((o) => o.value === params.cameraMovement);
    const itn = INTENSITY_OPTIONS.find((o) => o.value === params.motionIntensity);
    if (cam) {
      parts.push(
        itn && params.cameraMovement !== 'static' ? `${itn.label}速${cam.label}` : cam.label
      );
    }
  }
  return parts.join(' · ');
}

// ---------------------------------------------------------------------------
// 镜头预览覆盖层
// ---------------------------------------------------------------------------

function intensityToDuration(intensity: string | null | undefined): number {
  if (intensity === 'slow') return 3.5;
  if (intensity === 'fast') return 1;
  return 2;
}

interface OverlayProps {
  cameraMovement: string | null;
  motionIntensity: string | null;
}

function CameraPreviewOverlay({ cameraMovement, motionIntensity }: OverlayProps) {
  if (!cameraMovement) return null;
  const duration = intensityToDuration(motionIntensity);
  const animClass = (() => {
    switch (cameraMovement) {
      case 'static':
        return 'cam-preview-static';
      case 'push':
        return 'cam-preview-push';
      case 'pull':
        return 'cam-preview-pull';
      case 'pan_left':
        return 'cam-preview-pan-left';
      case 'pan_right':
        return 'cam-preview-pan-right';
      case 'tilt_up':
        return 'cam-preview-tilt-up';
      case 'tilt_down':
        return 'cam-preview-tilt-down';
      case 'orbit':
        return 'cam-preview-orbit';
      default:
        return 'cam-preview-static';
    }
  })();
  const isStatic = cameraMovement === 'static';
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <style>{CAMERA_PREVIEW_STYLES}</style>
      <div
        className={`cam-preview-frame ${animClass}`}
        style={{ animationDuration: isStatic ? undefined : `${duration}s` }}
      />
    </div>
  );
}

const CAMERA_PREVIEW_STYLES = `
.cam-preview-frame {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 70%;
  height: 70%;
  transform: translate(-50%, -50%);
  border: 1.5px dashed rgb(14 165 233 / 0.85);
  border-radius: 8px;
  box-shadow: 0 0 0 9999px rgb(14 165 233 / 0.04) inset;
  animation-iteration-count: infinite;
  animation-timing-function: ease-in-out;
}
.cam-preview-static {
  animation: none;
  border-style: solid;
  border-color: rgb(14 165 233 / 0.55);
}

@keyframes cam-push {
  0%   { transform: translate(-50%, -50%) scale(1.15); opacity: 0.45; }
  50%  { transform: translate(-50%, -50%) scale(0.7);  opacity: 0.95; }
  100% { transform: translate(-50%, -50%) scale(1.15); opacity: 0.45; }
}
.cam-preview-push { animation-name: cam-push; }

@keyframes cam-pull {
  0%   { transform: translate(-50%, -50%) scale(0.7);  opacity: 0.95; }
  50%  { transform: translate(-50%, -50%) scale(1.15); opacity: 0.45; }
  100% { transform: translate(-50%, -50%) scale(0.7);  opacity: 0.95; }
}
.cam-preview-pull { animation-name: cam-pull; }

@keyframes cam-pan-left {
  0%   { transform: translate(-25%, -50%); }
  50%  { transform: translate(-75%, -50%); }
  100% { transform: translate(-25%, -50%); }
}
.cam-preview-pan-left { animation-name: cam-pan-left; }

@keyframes cam-pan-right {
  0%   { transform: translate(-75%, -50%); }
  50%  { transform: translate(-25%, -50%); }
  100% { transform: translate(-75%, -50%); }
}
.cam-preview-pan-right { animation-name: cam-pan-right; }

@keyframes cam-tilt-up {
  0%   { transform: translate(-50%, -25%); }
  50%  { transform: translate(-50%, -75%); }
  100% { transform: translate(-50%, -25%); }
}
.cam-preview-tilt-up { animation-name: cam-tilt-up; }

@keyframes cam-tilt-down {
  0%   { transform: translate(-50%, -75%); }
  50%  { transform: translate(-50%, -25%); }
  100% { transform: translate(-50%, -75%); }
}
.cam-preview-tilt-down { animation-name: cam-tilt-down; }

@keyframes cam-orbit {
  0%   { transform: translate(-50%, -50%) rotate(0deg)   translate(28px) rotate(0deg); }
  100% { transform: translate(-50%, -50%) rotate(360deg) translate(28px) rotate(-360deg); }
}
.cam-preview-orbit { animation-name: cam-orbit; animation-timing-function: linear; }
`;
